import { cast, flow, getEnv, getSnapshot, Instance, types } from "mobx-state-tree";
import { Video } from "../inspection/Video";
import { FileStates, shopHistoryStorage } from "../shop/ShopHistoryStorage";
import { endpoints } from "../api/ApiEndpoints";
import { mainInfoStorage } from "./MainInfoStorageModel";
import { inspectionInfoStorage } from "./InspectionInfoStorageModel";
import { videoPlayerService } from "../video-player/VideoPlayerService";
import { emptyRect, Rect, RectType } from "../../models/Rect";
import { DetectionModel } from "../../models/DetectionModel";
import { ServiceModel } from "../app/ServiceStorage";
import jsPDF from "jspdf";
import { Pdf } from "../../components/pdf/PdfCreator";
import { Dayjs } from "dayjs";
import { snackbarNotificationsService } from "../notifications/SnackbarNotificationsService";

interface ILabel {
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  timestamp: number
}

interface IMainInformation {
    key: string,
    value: string
}

interface IDialog {
    screenshot: string,
    coordinates: any,
    timestamp: number
}

interface IDialogs {
    main: IDialog,
    detections: IDialog,
    distance: IDialog
}

const emptyDialog: IDialog = {
    screenshot: "",
    coordinates: null,
    timestamp: 0
}

const emptyDialogs: IDialogs = {
    main: emptyDialog,
    detections: emptyDialog,
    distance: emptyDialog
}

export interface OcrInput {
  detectMainInformation: boolean;
  detectMainInformationAtSeconds: number;
  mainInformationCoordinates: string;
  distanceInfoCoordinates: string;
  startInspectionDetectionFromSeconds: number;
  endInspectionDetectionAtSeconds: number;
  inspectionDetectionCoordinates: string;
}

// The user-reviewed comparison (OCR ground truth + accepted AI detections). Sent
// to the backend once when the user saves, and loaded back on reopen.
export interface OcrComparison {
  mainInformation: IMainInformation[];
  verifiedDetections: Instance<typeof DetectionModel>[];
}

const DialogModel = types.model({
    screenshot: types.string,
    coordinates: types.frozen<any>(),
    timestamp: types.number
})

const DialogsModel = types.model({
    main: DialogModel,
    detections: DialogModel,
    distance: DialogModel
})

export const OcrServiceModel = types.compose(
    ServiceModel,
    types.model({
        mainInformation: types.optional(types.array(types.frozen<IMainInformation>()), []),
        detections: types.optional(types.array(DetectionModel), []),
        screenshot: types.optional(types.frozen<string>(), ""),
        dialogs: types.optional(DialogsModel, emptyDialogs),
        pdfLoading: types.optional(types.frozen<boolean>(), false),
        userEndTime: types.maybeNull(types.number),
        comparison: types.optional(types.frozen<OcrComparison | null>(), null),
    })
).actions((self) => ({
  setCoordinates(rectType: RectType, label: ILabel, timestamp: number) {
    if (label == null)
      self.dialogs[rectType].coordinates = null;
    else {
      self.dialogs[rectType].timestamp = timestamp ?? label.timestamp ?? 0;
      self.dialogs[rectType].coordinates = {
        ...emptyRect,
        x: label.startX,
        y: label.startY,
        w: label.endX - label.startX,
        h: label.endY - label.startY
      }
    }
  },
  setUserEndTime(value: Dayjs | null) {
    self.userEndTime = value ? value.hour() * 3600 + value.minute() * 60 + value.second() : null;
  },
  // Local edit of the main information (key/value pairs). NOT persisted on its
  // own — it updates the in-memory state so the next comparison save (which
  // already sends mainInformation) writes the edits in one batch.
  setMainInformation(items: IMainInformation[]) {
    self.mainInformation = cast(items);
    mainInfoStorage.setMainInformation(items);
  },
  // Local detection edits (from the Detection List). They mutate the in-memory
  // detections and mirror to inspectionInfoStorage, which the Comparison tab
  // reads as its ground-truth baseline — so edits here flow into Comparison.
  // Persisted in one batch by the comparison Save.
  addDetection(detection: Partial<Instance<typeof DetectionModel>>) {
    const snap = getSnapshot(self.detections) as any[];
    self.detections = cast([
      ...snap,
      { ...detection, addedByUser: true, source: "User", decision: "Added" } as any,
    ]);
    inspectionInfoStorage.setDetections(getSnapshot(self.detections) as any);
  },
  updateDetection(index: number, patch: Partial<Instance<typeof DetectionModel>>) {
    const snap = (getSnapshot(self.detections) as any[]).slice();
    if (index < 0 || index >= snap.length) return;
    // A row the user added stays "Added"; anything else becomes "Edited", so the
    // audit trail distinguishes a new observation from a corrected one.
    const decision = snap[index]?.decision === "Added" ? "Added" : "Edited";
    snap[index] = { ...snap[index], ...patch, addedByUser: true, decision };
    self.detections = cast(snap);
    inspectionInfoStorage.setDetections(getSnapshot(self.detections) as any);
  },
  deleteDetection(index: number) {
    const snap = (getSnapshot(self.detections) as any[]).filter((_, i) => i !== index);
    self.detections = cast(snap);
    inspectionInfoStorage.setDetections(getSnapshot(self.detections) as any);
  },
})).extend(self => ({
    actions: {
        /**
         * A different file is being opened, so everything derived from the previous one is stale.
         *
         * Every reset used to be a side effect of request() succeeding: it assigns
         * mainInformation / detections from the response and pushes them into the two shared
         * stores. That works until the request does not run or does not succeed -- a file with no
         * OCR results, or a failure AppStorage.load() swallows per service -- and then the
         * previous file's header and detections stay on screen AND in the next save's payload,
         * because the page builds that payload from mainInformation.
         *
         * Guarded on the id actually changing, so a refresh or a redo of the file already open
         * does not blank the screen. Mirrors InspectionReviewService.setFileId, which has cleared
         * its own state this way all along.
         */
        setFileId(fileId: number) {
            if (self.fileId === fileId) return;
            self.fileId = fileId;

            self.mainInformation = cast([]);
            self.detections = cast([]);
            self.comparison = null;

            // Shared singletons: the page reads the header and the detection baseline from these,
            // and nothing else clears them -- reset() had no caller anywhere.
            mainInfoStorage.reset();
            inspectionInfoStorage.reset();
        },
        /**
         * "New": there is no file any more, so the service must be back in the state it is in
         * before a file has ever been opened -- NOT in the state the file it just dropped left
         * behind.
         *
         * The status used to be set to Failed here, which is a run outcome rather than the
         * absence of one. After "New" the header's Order & process panel therefore still listed
         * a run -- "Failed", "Not charged", with a Re-run button -- for a file that was no longer
         * open, instead of rendering nothing as it does for a fresh page. None is what a service
         * that has not run is in, and it is what the sibling analysis/segmentation services have
         * always cleared to.
         *
         * The results and the labeled regions go with it. Left behind, the previous file's
         * header and detections stay on screen and its ROIs still count as provided, so the next
         * upload could be run without labeling anything.
         */
        clear() {
            self.fileId = undefined;
            self.video = undefined;
            self.status = FileStates.None;
            self.startedAt = undefined;
            self.finishedAt = undefined;
            self.errorMessage = null;

            self.mainInformation = cast([]);
            self.detections = cast([]);
            self.comparison = null;
            self.screenshot = "";
            self.userEndTime = null;
            self.dialogs = cast(emptyDialogs);

            // Same shared singletons setFileId resets -- nothing else clears them.
            mainInfoStorage.reset();
            inspectionInfoStorage.reset();
        },
        request: flow(function* () {
            var endpoint = endpoints.get("media_file_ocr");
            endpoint.routeParams = [self.fileId!];

            const response = yield getEnv(self).api.get(endpoint);
            self.video = new Video(response.video);
            self.status = self.getFileState(response.ocrWasSuccessful, response.ocrErrorMessage, response.ocrIsInProgress);
            self.setTiming(response.ocrStartedAt, response.ocrFinishedAt);
            self.mainInformation = response.mainInformation ?? [];
            self.detections = response.detections ?? [];

            const timestampToSeconds = (ts: string | undefined): number | undefined => {
              if (!ts) return undefined;
              const date = new Date(ts).getTime();
              const currentDate = new Date(new Date().toDateString()).getTime();
              const seconds = (date - currentDate) / 1000;
              return seconds >= 0 ? seconds : undefined;
            };
            const firstDetectionSeconds = response.detections?.length > 0
              ? timestampToSeconds(response.detections[0].timestamp)
              : undefined;
            const lastDetectionSeconds = response.detections?.length > 0
              ? timestampToSeconds(response.detections[response.detections.length - 1].timestamp)
              : undefined;

            const mainTs = response.mainLabel?.timestamp || response.mainTimestamp;
            const detectionTs = response.detectionLabel?.timestamp
              || response.detectionTimestamp
              || firstDetectionSeconds;
            self.setCoordinates("main", response.mainLabel, mainTs);
            self.setCoordinates("detections", response.detectionLabel, detectionTs);
            self.setCoordinates("distance", response.distanceLabel, detectionTs);
            self.userEndTime = response.endTimestamp || lastDetectionSeconds || null;

            self.comparison = response.comparison ?? null;

            // Both, and in this order. The machine set is the baseline the "edited by reviewer"
            // markers are measured against and must be recorded before anything overlays it;
            // the displayed set starts out identical and the review overlay (applied by the page
            // once it has loaded) moves only the keys the reviewer actually changed.
            mainInfoStorage.setMachineMainInformation(getSnapshot(self.mainInformation) as any);
            mainInfoStorage.setMainInformation(getSnapshot(self.mainInformation) as any);
            inspectionInfoStorage.setDetections(getSnapshot(self.detections));
        }),
        saveComparison: flow(function* (payload: OcrComparison) {
            const endpoint = endpoints.get("media_file_ocr_comparison");
            endpoint.routeParams = [self.fileId!];
            yield getEnv(self).api.put(endpoint, payload);
            self.comparison = payload;
        }),
        runService: flow(function* () {
            var endpoint = endpoints.get("media_file_ocr");
            endpoint.routeParams = [self.fileId!];

            const request: OcrInput = {
                detectMainInformation: true,
                detectMainInformationAtSeconds: self.dialogs.main.timestamp,
                mainInformationCoordinates: mapCoordinates(self.dialogs.main.coordinates),
                distanceInfoCoordinates: mapCoordinates(self.dialogs.distance.coordinates),
                startInspectionDetectionFromSeconds: self.dialogs.detections.timestamp,
                endInspectionDetectionAtSeconds: self.userEndTime!,
                inspectionDetectionCoordinates: mapCoordinates(self.dialogs.detections.coordinates)
            }
            yield getEnv(self).api.put(endpoint, request);
            self.status = FileStates.Running;
        }),
        // Persist the labeling ROIs (main / detections / distance) WITHOUT running OCR.
        // Labeling is mandatory for every run; when the user runs Video Analysis without OCR
        // the page calls this first so the analysis job gets the distance region. Same payload
        // as runService, minus the status change (no OCR job starts). The stored labels load
        // back on reopen/Redo (see request()), so they are reused rather than re-drawn.
        saveLabels: flow(function* () {
            const endpoint = endpoints.get("media_file_ocr_labels");
            endpoint.routeParams = [self.fileId!];

            const request: OcrInput = {
                detectMainInformation: true,
                detectMainInformationAtSeconds: self.dialogs.main.timestamp,
                mainInformationCoordinates: mapCoordinates(self.dialogs.main.coordinates),
                distanceInfoCoordinates: mapCoordinates(self.dialogs.distance.coordinates),
                startInspectionDetectionFromSeconds: self.dialogs.detections.timestamp,
                endInspectionDetectionAtSeconds: self.userEndTime!,
                inspectionDetectionCoordinates: mapCoordinates(self.dialogs.detections.coordinates)
            }
            yield getEnv(self).api.put(endpoint, request);
        }),
        checkStatus: flow(function* () {
            if (!self.fileId) return;
            const endpoint = endpoints.get("media_file_ocr");
            endpoint.routeParams = [self.fileId];
            const response = yield getEnv(self).api.get(endpoint);
            const newStatus = self.getFileState(
              response.ocrWasSuccessful,
              response.ocrErrorMessage,
              response.ocrIsInProgress
            );
            if (newStatus !== self.status) {
              self.status = newStatus;
              if (newStatus === FileStates.Success || newStatus === FileStates.Failed) {
                yield (self as any).load();
              }
            }
        }),
        pollUntilReady: flow(function* () {
            if (!self.fileId) return;
            const endpoint = endpoints.get("media_file_ocr");
            endpoint.routeParams = [self.fileId];
            const response = yield getEnv(self).api.get(endpoint);
            const wasConverted = !!self.video?.isConverted;
            const isConvertedNow = !!response.video?.isConverted;
            const ocrJustDone = response.ocrWasSuccessful && self.status !== FileStates.Success;
            const ocrJustFailed = !!response.ocrErrorMessage && self.status !== FileStates.Failed;
            if ((!wasConverted && isConvertedNow) || ocrJustDone || ocrJustFailed) {
              yield (self as any).load();
            }
        }),
        runAfterUpload: flow(function* () {
            var endpoint = endpoints.get("media_file_ocr");
            endpoint.routeParams = [self.fileId!];
            yield getEnv(self).api.put(endpoint, null);
            yield self.load();
            // Refresh the global shop-history so the left-menu running-count badge
            // immediately reflects the just-started OCR for a new upload (Path 1).
            yield shopHistoryStorage.load();
        }),
        redo: flow(function* () {
          self.status = FileStates.None;
          self.mainInformation = cast([]);
          self.detections = cast([]);
        }),
    }
})).views((self) => ({
    get inputProvided() {
        return this.mainInfoDone && this.detectionsDistanceDone;
    },
    get mainInfoDone() {
        return self.dialogs.main.coordinates !== null && self.dialogs.main.coordinates !== undefined;
    },

    get detectionsDistanceDone() {
        return (
            self.dialogs.detections.coordinates !== null && self.dialogs.distance.coordinates !== null
            && self.dialogs.detections.coordinates !== undefined && self.dialogs.distance.coordinates !== undefined
        );
    },
    get ocrNotStarted(): boolean {
      return self.status === FileStates.None;
    },
    get ocrFailed(): boolean {
      return self.status === FileStates.Failed;
    },
    get ocrRunning(): boolean {
      return self.status === FileStates.Running;
    },
    // True when the URL the user landed on corresponds to a backup zip that is
    // still being restored on the server. We detect this by combining the
    // Running status (OcrIsInProgress=true on the row) with a filename / content
    // type that's clearly a .zip placeholder — a real OCR file is video/mp4.
    get isBeingRestored(): boolean {
      if (self.status !== FileStates.Running) return false;
      const name = (self.video?.name ?? "").toLowerCase();
      const ct = (self.video?.contentType ?? "").toLowerCase();
      return name.endsWith(".zip") || ct.includes("zip");
    },
    get ocrSuccessful(): boolean {
      return self.status === FileStates.Success;
    },
    // Identifier embedded in the backup zip name — the video's actual file
    // name (without extension). Falls back to the file id when the name is
    // missing.
    get videoIdentifier(): string {
      const name = self.video?.name;
      if (name && name.length > 0) return name.replace(/\.[^/.]+$/, "");
      return self.fileId !== undefined ? String(self.fileId) : "";
    }
})).actions((self) => {
    const downloadOcr = flow(function*() {
        const endpoint = endpoints.get("media_file_ocr_export");
        endpoint.routeParams = [self.fileId!];
        const response = yield getEnv(self).api.download(endpoint);
        return response.data;
    })
    const downloadIsybauFile = flow(function*() {
        const endpoint = endpoints.get("media_file_ocr_export_isybau");
        endpoint.routeParams = [self.fileId!];
        const response = yield getEnv(self).api.download(endpoint);
        return response.data;
    })
    // Generated from an APPROVED review of the video-analysis results. A separate document
    // from the OCR pipeline's export above, under its own file name, so a run with both
    // services offers both.
    const downloadVideoAnalysisIsybauFile = flow(function*() {
        const endpoint = endpoints.get("media_file_video_analysis_export_isybau");
        endpoint.routeParams = [self.fileId!];
        const response = yield getEnv(self).api.download(endpoint);
        return response.data;
    })
    const downloadVideoAnalysisDwaFile = flow(function*() {
        const endpoint = endpoints.get("media_file_video_analysis_export_dwa");
        endpoint.routeParams = [self.fileId!];
        const response = yield getEnv(self).api.download(endpoint);
        return response.data;
    })
    const downloadApprovedOcrIsybauFile = flow(function*() {
        const endpoint = endpoints.get("media_file_approved_ocr_export_isybau");
        endpoint.routeParams = [self.fileId!];
        const response = yield getEnv(self).api.download(endpoint);
        return response.data;
    })
    const downloadApprovedOcrDwaFile = flow(function*() {
        const endpoint = endpoints.get("media_file_approved_ocr_export_dwa");
        endpoint.routeParams = [self.fileId!];
        const response = yield getEnv(self).api.download(endpoint);
        return response.data;
    })
    const downloadApprovedOcrResultsFile = flow(function*() {
        const endpoint = endpoints.get("media_file_approved_ocr_export_results");
        endpoint.routeParams = [self.fileId!];
        const response = yield getEnv(self).api.download(endpoint);
        return response.data;
    })
    const downloadApprovedOcrCsvFile = flow(function*() {
        const endpoint = endpoints.get("media_file_approved_ocr_export_csv");
        endpoint.routeParams = [self.fileId!];
        const response = yield getEnv(self).api.download(endpoint);
        return response.data;
    })
    const downloadDwaFile = flow(function*() {
        const endpoint = endpoints.get("media_file_ocr_export_dwa");
        endpoint.routeParams = [self.fileId!];
        const response = yield getEnv(self).api.download(endpoint);
        return response.data;
    })
    return {
        setPdfLoading(loading: boolean) {
          self.pdfLoading = loading;
        },
        setScreenshotUrl(url: string) {
          self.screenshot = url;
        },
        setDialogScreenshot(rectTypes: RectType[], url: string) {
          rectTypes.forEach((rectType: RectType) => {
            self.dialogs[rectType].screenshot = url;
          });
        },
        setDialogCoordinates(rectTypes: RectType[], rects: Map<RectType, Rect>) {
          rectTypes.forEach((rectType: RectType) => {
            self.dialogs[rectType].coordinates = rects.get(rectType);
          });
        },
        setDialogTimestamp(rectTypes: RectType[], timestamp: number) {
          rectTypes.forEach((rectType: RectType) => {
            self.dialogs[rectType].timestamp = timestamp;
          });
        },
        getRects(rectTypes: RectType[]) {
          let rects: Map<RectType, Rect> = new Map();

          rectTypes.forEach((rectType: RectType) => {
            if (self.dialogs[rectType].coordinates) {
              let { x, y, h, w } = self.dialogs[rectType].coordinates;
              let rect: Rect = { x: x, y: y, h: h, w: w, selected: false, visible: true };
              rects.set(rectType, rect);
            }
          });
          return rects;
        },
        async downloadCsv() {
          const blob = await downloadOcr();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'ocr.zip';
          a.click();
          URL.revokeObjectURL(url);
        },
        async downloadIsybau() {
          try {
            const blob = await downloadIsybauFile();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'ISYBAU_2017-07.xml';
            a.click();
            URL.revokeObjectURL(url);
          } catch (error) {
            snackbarNotificationsService.showError("general.export.xml.error");
          }
        },
        async downloadVideoAnalysisIsybau() {
          try {
            const blob = await downloadVideoAnalysisIsybauFile();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'ISYBAU_2017-07_VideoAnalysis.xml';
            a.click();
            URL.revokeObjectURL(url);
          } catch (error) {
            snackbarNotificationsService.showError("comparison.export.video-analysis-xml.error");
          }
        },
        async downloadVideoAnalysisDwa() {
          try {
            const blob = await downloadVideoAnalysisDwaFile();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'DWA-M150_04-2010_VideoAnalysis.xml';
            a.click();
            URL.revokeObjectURL(url);
          } catch (error) {
            snackbarNotificationsService.showError("comparison.export.video-analysis-xml.error");
          }
        },
        async downloadApprovedOcrIsybau() {
          try {
            const blob = await downloadApprovedOcrIsybauFile();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Approved_ISYBAU_2017-07.xml';
            a.click();
            URL.revokeObjectURL(url);
          } catch (error) {
            snackbarNotificationsService.showError("comparison.export.approved-xml.error");
          }
        },
        async downloadApprovedOcrDwa() {
          try {
            const blob = await downloadApprovedOcrDwaFile();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Approved_DWA-M150_04-2010.xml';
            a.click();
            URL.revokeObjectURL(url);
          } catch (error) {
            snackbarNotificationsService.showError("comparison.export.approved-xml.error");
          }
        },
        async downloadApprovedOcrResults() {
          try {
            const blob = await downloadApprovedOcrResultsFile();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Approved_OCR_results.json';
            a.click();
            URL.revokeObjectURL(url);
          } catch (error) {
            snackbarNotificationsService.showError("comparison.export.approved-xml.error");
          }
        },
        async downloadApprovedOcrCsv() {
          try {
            const blob = await downloadApprovedOcrCsvFile();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Approved_ocr.csv';
            a.click();
            URL.revokeObjectURL(url);
          } catch (error) {
            snackbarNotificationsService.showError("comparison.export.approved-xml.error");
          }
        },
        async downloadDwa() {
          try {
            const blob = await downloadDwaFile();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'DWA-M150_04-2010.xml';
            a.click();
            URL.revokeObjectURL(url);
          } catch (error) {
            snackbarNotificationsService.showError("general.export.xml.error");
          }
        },
        async downloadPdf() {
          this.setPdfLoading(true);
          try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            var file = new Pdf(
              pdf.internal.pageSize.getWidth(),
              pdf.internal.pageSize.getHeight(),
              10,
              pdf
            );

            const tables = document.querySelectorAll("#pdf-content-table")
            await file.addElements(tables);
            const listHeader = document.querySelectorAll("#pdf-content-list-header");
            await file.addElements(listHeader, { allowSlicing: false, startNewPage: true });
            const cards = document.querySelectorAll("#pdf-content-card");
            await file.addElements(cards, { allowSlicing: false });

            file.save(`inspection_report.pdf`);
          } catch (error) {
            // Nothing here used to catch: a throw anywhere in the capture escaped as an
            // unhandled rejection (the red overlay in dev, silence in production) and left
            // pdfLoading stuck true, so the export button spun for the rest of the session.
            console.error("PDF export failed", error);
            snackbarNotificationsService.showError("general.export.pdf.error");
          } finally {
            this.setPdfLoading(false);
          }
        }
    }
})

function mapCoordinates(coordinates: Rect | null | undefined) {
    // Null when the labeling rectangle was never set (e.g. a file reopened without its
    // OCR labels). Callers must guard on `inputProvided`; returning "" here is a safety
    // net so a stray call never crashes the UI with "Cannot read properties of null".
    if (!coordinates) return "";
    return `${coordinates.x} ${coordinates.y} ${coordinates.x + coordinates.w} ${
      coordinates.y + coordinates.h
    }`;
  }

export type OcrService = Instance<typeof OcrServiceModel>
